import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { generateEmailTemplate } from '@/services/communication/emailTemplates';
import { generateCallScript } from '@/services/communication/callScripts';
import { saveTemplate } from '@/services/communication/templates';
import { getLeadById } from '@/services/leads';
import { Lead } from '@/types/lead';
import { useAuth } from '@/context/AuthContext';

export const useCommunicationTemplates = (leadId: string) => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [lead, setLead] = useState<Lead | null>(null);
  const [emailTemplate, setEmailTemplate] = useState<string>('');
  const [callScript, setCallScript] = useState<string>('');
  const [isLoadingEmail, setIsLoadingEmail] = useState<boolean>(false);
  const [isLoadingCall, setIsLoadingCall] = useState<boolean>(false);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    const fetchLead = async () => {
      try {
        const leadData = await getLeadById(leadId);
        if (!cancelled && leadData) {
          setLead(leadData);
        }
      } catch (error) {
        console.error('Error fetching lead details:', error);
      }
    };
    fetchLead();
    return () => { cancelled = true; };
  }, [leadId]);

  const getSenderInfo = useCallback(() => {
    return {
      senderName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '',
      senderRole: profile?.job_title || 'Sales Representative',
      senderCompany: profile?.company_id ? 'Your Company' : '',
      senderEmail: profile?.work_email || '',
      senderPhone: profile?.work_phone || '',
      senderContactNumber: (profile as any)?.contact_number || '',
    };
  }, [profile]);

  const handleGenerateEmailTemplate = useCallback(async () => {
    if (!leadId) return;
    setIsLoadingEmail(true);
    try {
      const template = await generateEmailTemplate(leadId, getSenderInfo());
      if (template) {
        setEmailTemplate(template);
        toast({ title: "Email Template Generated", description: "Your email template has been created successfully." });
      } else {
        toast({ variant: "destructive", title: "Error", description: "Failed to generate email template." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "An error occurred while generating the email template." });
    } finally {
      setIsLoadingEmail(false);
    }
  }, [leadId, getSenderInfo, toast]);

  const handleGenerateEmailWithPrompt = useCallback(async (customPrompt: string) => {
    if (!leadId) return;
    setIsLoadingEmail(true);
    try {
      const template = await generateEmailTemplate(leadId, getSenderInfo(), customPrompt);
      if (template) {
        setEmailTemplate(template);
        toast({ title: "Email Template Generated", description: "Your customized email template has been created successfully." });
      } else {
        toast({ variant: "destructive", title: "Error", description: "Failed to generate email template." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "An error occurred while generating the email template." });
    } finally {
      setIsLoadingEmail(false);
    }
  }, [leadId, getSenderInfo, toast]);

  const handleGenerateCallScript = useCallback(async () => {
    if (!leadId) return;
    setIsLoadingCall(true);
    try {
      const script = await generateCallScript(leadId, getSenderInfo());
      if (script) {
        setCallScript(script);
        toast({ title: "Call Script Generated", description: "Your call script has been created successfully." });
      } else {
        toast({ variant: "destructive", title: "Error", description: "Failed to generate call script." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "An error occurred while generating the call script." });
    } finally {
      setIsLoadingCall(false);
    }
  }, [leadId, getSenderInfo, toast]);

  const handleGenerateCallScriptWithPrompt = useCallback(async (customPrompt: string) => {
    if (!leadId) return;
    setIsLoadingCall(true);
    try {
      const script = await generateCallScript(leadId, getSenderInfo(), customPrompt);
      if (script) {
        setCallScript(script);
        toast({ title: "Call Script Generated", description: "Your customized call script has been created successfully." });
      } else {
        toast({ variant: "destructive", title: "Error", description: "Failed to generate call script." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "An error occurred while generating the call script." });
    } finally {
      setIsLoadingCall(false);
    }
  }, [leadId, getSenderInfo, toast]);

  const saveCurrentEmailTemplate = useCallback(async (content: string) => {
    if (!content) {
      toast({ variant: "destructive", title: "Error", description: "No email template content to save." });
      return;
    }
    try {
      const templateName = `Email for ${lead?.name || 'lead'} (${lead?.status || 'new'})`;
      const result = await saveTemplate({ name: templateName, content, type: 'email' as const, leadStatus: lead?.status, leadId });
      if (result) {
        toast({ title: "Success", description: "Email template saved successfully." });
      } else {
        throw new Error("Failed to save template");
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to save email template." });
    }
  }, [lead, leadId, toast]);

  const saveCurrentCallScript = useCallback(async (content: string) => {
    if (!content) {
      toast({ variant: "destructive", title: "Error", description: "No call script content to save." });
      return;
    }
    try {
      const templateName = `Call script for ${lead?.name || 'lead'} (${lead?.status || 'new'})`;
      const result = await saveTemplate({ name: templateName, content, type: 'call' as const, leadStatus: lead?.status, leadId });
      if (result) {
        toast({ title: "Success", description: "Call script saved successfully." });
      } else {
        throw new Error("Failed to save call script");
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to save call script." });
    }
  }, [lead, leadId, toast]);

  return {
    lead,
    emailTemplate,
    callScript,
    isLoadingEmail,
    isLoadingCall,
    handleGenerateEmailTemplate,
    handleGenerateEmailWithPrompt,
    handleGenerateCallScript,
    handleGenerateCallScriptWithPrompt,
    saveCurrentEmailTemplate,
    saveCurrentCallScript
  };
};
